import React, { useState } from "react";
import { Box, Paper, useTheme, useMediaQuery } from "@mui/material";
import Sidebar from "./Sidebar";
import Header from "./Header";
import ChatArea from "../ChatArea";
import RightPanel from "../RightPanel";
import { forceCreateSessionForUser } from "../../utils/sessionManager";
import { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";

const Layout = ({
  darkMode,
  setDarkMode,
  history,
  onFileParsed,
  onClearHistory,
  onOpenHistory,
  onNewChat,
  activeConvId,
  rows,
  schema,
  onMessage,
  onSchemaUpdate,
  chatRef,
}) => {
  const { user } = useContext(AuthContext);
  const [showPreview, setShowPreview] = useState(false);
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(280); // expanded width
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down("sm"));

  // Determine effective sidebar width based on collapse state and screen size
  const collapsedWidth = 80;
  const effectiveSidebarWidth =
    sidebarCollapsed || isSmDown ? collapsedWidth : sidebarWidth;

  const togglePreview = () => setShowPreview((prev) => !prev);

  const handleOpenHistory = (conv) => {
    if (!conv) return;
    onOpenHistory(conv);
  };


  // const handleNewChat = async () => {
  //   if (!user?.userId) {
    
  //     return;
  //   }

  //   try {
  //     // Always use the logged-in user's ID
  //     const sid = await forceCreateSessionForUser(user.userId);
    
  //   } catch (e) {
     
  //   }

  //   onNewChat?.();
  //   setShowPreview(false);
  //   setIsFileUploaded(false);
  // };

const handleNewChat = async () => {
  if (!user?.userId) return;

  try {
    // Always use the logged-in user's ID
    const sid = await forceCreateSessionForUser(user.userId);
  } catch (e) {}

  onNewChat?.();
  setShowPreview(false);
  setIsFileUploaded(false);

  // Hide WelcomeScreen in ChatArea when new chat is clicked
  if (chatRef?.current?.hideWelcome) {
    chatRef.current.hideWelcome();
  }
};


  const handleFileParsed = (rows, filename, file) => {
    setIsFileUploaded(true);
    onFileParsed?.(rows, filename, file);
  };

  const hasData =
    rows?.length > 0 || (schema && Object.keys(schema).length > 0);

  const currentConv = history.find((conv) => conv.id === activeConvId);
  const currentMessages = currentConv?.messages || [];

  // Optional: collapse sidebar automatically on small screens
  React.useEffect(() => {
    if (isSmDown) {
      setSidebarCollapsed(true);
    } else {
      setSidebarCollapsed(false);
    }
  }, [isSmDown]);

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        flexDirection: "column",
      }}
    >
      {/* Header */}
      <Header
        darkMode={darkMode}
        setDarkMode={setDarkMode}
        onNewChat={handleNewChat}
        sidebarWidth={effectiveSidebarWidth}
      />

      <Box
        sx={{
          display: "flex",
          flex: 1,
          overflow: "hidden",
          pt: "60px",
          flexDirection: isSmDown ? "column" : "row",
        }}
      >
        {/* Sidebar - always visible, collapsible */}
        <Sidebar
          history={history}
          onClearHistory={onClearHistory}
          onFileParsed={handleFileParsed}
          onOpenHistory={handleOpenHistory}
          setSidebarWidth={setSidebarWidth}
          activeConvId={activeConvId}
          collapsed={sidebarCollapsed}
          chatRef={chatRef}
          setCollapsed={setSidebarCollapsed}
          sx={{
            width: effectiveSidebarWidth,
            flexShrink: 0,
            borderRight: `1px solid ${theme.palette.divider}`,
            height: isSmDown ? "auto" : `calc(100vh - 60px)`,
            position: isSmDown ? "relative" : "sticky",
            top: 60,
            overflowY: "auto",
            transition: "width 0.3s ease",
          }}
        />

        {/* Main Content */}
        <Box
          sx={{
            display: "flex",
            flex: 1,
            overflow: "hidden",
            gap: 1,
            pt: 1,
            ml: isSmDown ? "80px" : "2px",
            flexDirection: isSmDown ? "column" : "row",
            bgcolor: "background.default",
          }}
        >
          {/* ChatArea */}
          <Paper
            sx={{
              flexGrow: 1,
              flexBasis: showPreview && !isSmDown ? "50%" : "100%",
              transition: "flex-basis 0.3s ease",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
              borderRadius: 2,
              boxShadow: 3,
              height: isSmDown ? "50vh" : "auto",
            }}
          >
            <ChatArea
              ref={chatRef}
              rows={rows}
              schema={schema}
              messages={currentMessages}
              onMessage={onMessage}
              onSchemaUpdate={onSchemaUpdate}
              onFileParsed={handleFileParsed}
              darkMode={darkMode}
              togglePreview={togglePreview}
              isPreviewOpen={showPreview}
              isFileUploaded={isFileUploaded}
            />
          </Paper>

          {/* RightPanel */}
          {hasData && (
            <Paper
              sx={{
                flexGrow: showPreview && !isSmDown ? 1 : 0,
                flexBasis: showPreview && !isSmDown ? "50%" : 0,
                transition: "flex-basis 0.3s ease, opacity 0.3s ease",
                opacity: showPreview && !isSmDown ? 1 : 0,
                display: showPreview && !isSmDown ? "flex" : "none",
                flexDirection: "column",
                overflow: "hidden",
                borderRadius: 2,
                boxShadow: 3,
                height: isSmDown ? "50vh" : "auto",
              }}
            >
              {showPreview && (
                <RightPanel
                  rows={rows}
                  schema={schema}
                  history={history}
                  darkMode={darkMode}
                />
              )}
            </Paper>
          )}

          {/* On small screens, show RightPanel below ChatArea when preview is open */}
          {hasData && showPreview && isSmDown && (
            <Box
              sx={{
                height: "50vh",
                mt: 1,
                borderRadius: 2,
                boxShadow: 3,
                overflow: "hidden",
              }}
            >
              <RightPanel
                rows={rows}
                schema={schema}
                history={history}
                darkMode={darkMode}
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
